import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  MapPin, Phone, Check, AlertCircle, Loader,
  Link2, Palette, ExternalLink, ShoppingBag
} from 'lucide-react';
import '../Boutiques.css';

const AVATAR_COLORS = ['#6366f1','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4'];
const avatarColor = (i) => AVATAR_COLORS[i % AVATAR_COLORS.length];

export default function EcommerceVitrines() {
  const navigate = useNavigate();
  const [boutiques, setBoutiques] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [toast,     setToast]     = useState({ type: '', text: '' });

  useEffect(() => { fetchBoutiques(); }, []);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast({ type: '', text: '' }), 4000);
  };

  const fetchBoutiques = async () => {
    try {
      const { data } = await axios.get('/api/boutiques');
      setBoutiques(data);
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur de chargement');
    } finally { setLoading(false); }
  };

  const shopUrl = (b) => `${window.location.origin}/shop/${b._id}`;

  const copyShopLink = async (b) => {
    try {
      await navigator.clipboard.writeText(shopUrl(b));
      showToast('success', `Lien copié — ${b.nom}`);
    } catch {
      showToast('error', 'Impossible de copier le lien');
    }
  };

  if (loading) return (
    <div className="bt-loading"><Loader className="bt-spinner" size={26}/><span>Chargement…</span></div>
  );

  return (
    <div className="bt-page">
      {toast.text && (
        <div className={`bt-toast bt-toast--${toast.type}`}>
          {toast.type === 'success' ? <Check size={14}/> : <AlertCircle size={14}/>}
          {toast.text}
        </div>
      )}

      <div className="bt-header">
        <div className="bt-header-left">
          <div className="bt-header-icon"><Palette size={20}/></div>
          <div>
            <h1 className="bt-header-title">Vitrines & Liens</h1>
            <p className="bt-header-sub">Personnalisez la vitrine publique de chaque boutique et partagez son lien</p>
          </div>
        </div>
      </div>

      <div className="bt-content">
        {boutiques.length === 0 ? (
          <div className="bt-empty">
            <div className="bt-empty-icon"><ShoppingBag size={30}/></div>
            <h3>Aucune boutique</h3>
            <p>Créez une boutique pour pouvoir publier sa vitrine en ligne</p>
          </div>
        ) : (
          <div className="bt-card">
            <table className="bt-table">
              <thead>
                <tr>
                  <th>Boutique</th>
                  <th>Lien public</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {boutiques.map((b, i) => {
                  const color = avatarColor(i);
                  return (
                    <tr key={b._id} className={!b.actif ? 'bt-row--dim' : ''}>
                      <td>
                        <div className="bt-cell-ent">
                          <div className="bt-av" style={{ background: color + '18', color }}>
                            {b.nom?.charAt(0)?.toUpperCase() || 'B'}
                          </div>
                          <div>
                            <div className="bt-cell-name">{b.nom}</div>
                            {b.adresse && <div className="bt-cell-sub"><MapPin size={10}/> {b.adresse}</div>}
                            {b.telephone && <div className="bt-cell-sub"><Phone size={10}/> {b.telephone}</div>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <a href={shopUrl(b)} target="_blank" rel="noreferrer" className="bt-shop-link">
                          {shopUrl(b)} <ExternalLink size={11}/>
                        </a>
                      </td>
                      <td>
                        <span className={`bt-badge ${b.actif ? 'bt-badge--active' : 'bt-badge--inactive'}`}>
                          {b.actif ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="bt-actions">
                          <button className="bt-action-btn bt-action-btn--shop"
                            onClick={() => copyShopLink(b)} title="Copier le lien de la vitrine publique">
                            <Link2 size={12}/> Lien
                          </button>
                          <button className="bt-action-btn bt-action-btn--vitrine"
                            onClick={() => navigate(`/boutiques/${b._id}/vitrine`)} title="Personnaliser la vitrine publique">
                            <Palette size={12}/> Vitrine
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
